import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { UserService } from '../../../../services/user.service';
import { LoggerService } from '../../../../shared/services/logger.service';
import { ListService } from '../../../../shared/services/list.service';
import { PopupDialogComponent } from '../../../../shared/components/popup-dialog/popup-dialog.component';
import { DropdownSelectComponent } from '../../../../shared/components/dropdown-select/dropdown-select.component';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { BillingService } from '../../../../services/billing.service';
import { LookupService } from '../../../../services/lookup.service';

type LedgerSource = 'CLAIM' | 'CDR' | 'SPR' | 'MANUAL' | 'WRITE_OFF';
type LedgerStatus = string;


interface LedgerRow {
  source: LedgerSource;
  eventDate: Date;
  pplIcn: string;
  consumerName: string;
  pplId: string;
  mco: string;
  status: LedgerStatus;
  amount: number;
  claimDetailId: number | string | null;
  sourceNote: string | null;
  isWrittenOff: boolean;
  consumerDisplay: string;
  eventDateDisplay: string;
  freqDisplay: string;
  arAmount: string;
  arAmountClass: string;
  statusClass: string;
  sourceBadgeClass: string;
  charges?: number;
  payments?: number;
  openBal?: number;
  writtenOff?: number;
  woPendingId?: string | null;
}

type WriteOffStatus = 'Pending' | 'Approved' | 'Rejected';

interface WriteOffRequest {
  id: string;
  claimDetailId: string | null;
  pplIcn: string;
  consumerName: string;
  pplId: string;
  mco: string;
  dos: Date;
  totalCharges: number;
  payments: number;
  arAmount: number;
  writeOffAmount: number;
  reason: string;
  requestedBy: string;
  requestedOn: Date;
  status: WriteOffStatus;
  decidedBy: string | null;
  decidedOn: Date | null;
  comment: string;
}

@Component({
  selector: 'ppl-global-ledger-list',
  standalone: true,
  imports: [CommonModule, FormsModule, NzDatePickerModule, PopupDialogComponent, DropdownSelectComponent, ListTableComponent],
  templateUrl: './global-ledger-list.component.html',
  styleUrl: './global-ledger-list.component.css',
})
export class GlobalLedgerListComponent implements OnInit {
  programCode = '';
  mcoOptions: string[] = [];

  currentUser = 'You (Billing Supervisor)';
  today = new Date();

  activeTab: 'ledger' | 'approvals' = 'ledger';

  feedback = '';
  feedbackIsError = false;

  // ---------------------------------------------------------------------------
  // Ledger Entries tab
  // ---------------------------------------------------------------------------
  filterDosFrom: Date | null = null;
  filterDosTo: Date | null = null;
  filterMco = '';
  filterIcn = '';
  filterConsumer = '';
  searchError = '';
  searched = false;
  results: LedgerRow[] = [];
  exportSubmitting = false;
  exportError = '';

  ledgerCols = [
    { field: 'source', header: 'Source' },
    { field: 'eventDateDisplay', header: 'Event Date' },
    { field: 'pplIcn', header: 'PPL ICN' },
    { field: 'consumerDisplay', header: 'Consumer' },
    { field: 'mco', header: 'MCO' },
    { field: 'freqDisplay', header: 'Frequency Code' },
    { field: 'status', header: 'Status' },
    { field: 'arAmount', header: 'Amount' },
  ];

  ledgerButtonConfig = [
    {
      key: 'writeOff',
      icon: '',
      label: () => 'Write Off',
      disabled: () => false,
      condition: (row: LedgerRow) => this.showsWriteOffButton(row),
    },
  ];

  // ---------------------------------------------------------------------------
  // Write-off Approvals tab
  // ---------------------------------------------------------------------------
  apStatus: WriteOffStatus | 'All' = 'Pending';
  apSearch = '';
  apMco = '';
  requests: WriteOffRequest[] = [];
  selectedIds = new Set<string>();

  showConfirmModal = false;
  confirmIds: string[] = [];

  // Write Off request (Ledger Entries tab)
  showWriteOffModal = false;
  woRow: LedgerRow | null = null;
  woReasonOptions: string[] = [];
  woReason = '';
  woComment = '';
  woReasonError = '';
  woSubmitError = '';
  woSubmitting = false;

  constructor(
    private userService: UserService,
    private logger: LoggerService,
    private listService: ListService,
    private billingService: BillingService,
    private lookupService: LookupService
  ) {}

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programCode = selectedProgram?.program_code ?? '';
    this.onSearch();
    this.loadWriteOffReasons();
    this.loadWriteOffRequests();
    this.loadMcoOptions();
  }

  // Same org lookup used by Consumer Ledger and Deposit Entry's MCO dropdowns.
  private loadMcoOptions(): void {
    this.lookupService.getOrgData(this.programCode || 'NY').subscribe({
      next: (response: any) => {
        const orgs = response?.responsedata ?? [];
        this.mcoOptions = orgs
          .filter((o: any) => o.orgTypeLookup === 'MCO')
          .map((o: any) => o.orgName)
          .sort();
      },
      error: (err: any) => {
        this.logger.error('Failed to load MCO org list', err);
      },
    });
  }

  private loadWriteOffReasons(): void {
    this.billingService.getWriteOffReasons(this.programCode || 'NY').subscribe({
      next: (response: any) => {
        this.woReasonOptions = response?.responsedata ?? [];
      },
      error: (err: any) => {
        this.logger.error('Failed to load write-off reasons', err);
        this.woReasonOptions = [];
      },
    });
  }

  showTab(tab: 'ledger' | 'approvals'): void {
    this.activeTab = tab;
  }

  // ---------------------------------------------------------------------------
  // Ledger Entries tab
  // ---------------------------------------------------------------------------

  // Only validation still enforced: From can't be after To, when both are given.
  private validateFilters(): boolean {
    this.searchError = '';
    const from = this.filterDosFrom;
    const to = this.filterDosTo;

    if (from && to && from > to) {
      this.searchError = 'Date of Service: From must be on or before To.';
    }

    return !this.searchError;
  }

  onSearch(): void {
    if (!this.validateFilters()) {
      return;
    }

    const searchParam: { jsonPath: string; operator: string; value: string }[] = [];
    if (this.filterDosFrom) {
      searchParam.push({ jsonPath: 'serviceDate', operator: 'greaterthanorequal', value: this.toMmDdYyyy(this.filterDosFrom) });
    }
    if (this.filterDosTo) {
      searchParam.push({ jsonPath: 'serviceDate', operator: 'lessthanorequal', value: this.toMmDdYyyy(this.filterDosTo) });
    }
    if (this.filterMco) {
      searchParam.push({ jsonPath: 'mcoName', operator: 'equal', value: this.filterMco });
    }
    const icn = this.filterIcn.trim();
    if (icn) {
      searchParam.push({ jsonPath: 'pplIcn', operator: 'equal', value: icn });
    }
    const consumer = this.filterConsumer.trim();
    if (consumer) {
      searchParam.push({ jsonPath: 'participantPplId', operator: 'equal', value: consumer });
    }

    const payload = {
      pageName: 'globalLedgerView',
      programCode: this.programCode || 'NY',
      recordsPerPage: 50,
      startingPageNumber: 1,
      orderByColumn: ['serviceDate'],
      orderBy: ['desc'],
      condition: 'and',
      searchParam,
    };

    this.listService.getListData(payload, 'billing').subscribe({
      next: (response: any) => {
        if (response?.responsemsg?.[0]?.code === 'INTERNAL_SERVER_ERROR') {
          return;
        }
        const rows: any[] = response?.responsedata ?? [];
        this.results = rows.map((r) => this.mapLedgerRow(r));
        this.searched = true;
      },
      error: (err) => {
        this.logger.error('Failed to load global ledger', err);
        this.results = [];
        this.searched = true;
      },
    });
  }

  private toMmDdYyyy(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`;
  }

  private mapLedgerRow(r: any): LedgerRow {
    const source = (r?.entryType ?? 'CLAIM') as LedgerSource;
    const consumerName = r?.requestedByName ?? '-';
    const pplId = r?.participantPplId ?? '-';
    const status = (r?.status ?? '-') as LedgerStatus;
    const amount = Number(r?.amount ?? 0);
    const charges = r?.charges != null ? Number(r.charges) : amount;
    const payments = r?.payments != null ? Number(r.payments) : 0;
    const eventDate = r?.serviceDate ? new Date(r.serviceDate) : r?.eventDate ? new Date(r.eventDate) : new Date();

    return {
      source,
      eventDate,
      pplIcn: r?.pplIcn ?? '-',
      consumerName,
      pplId,
      mco: r?.mcoName ?? '-',
      status,
      amount,
      claimDetailId: r?.tasBillingBatchClaimDetailId ?? null,
      sourceNote: r?.sourceNote ?? null,
      isWrittenOff: r?.isWrittenOff === true,
      consumerDisplay:
        pplId !== '-' && consumerName !== '-'
          ? `${consumerName} ${pplId}`
          : pplId !== '-'
            ? pplId
            : consumerName,
      eventDateDisplay: this.toMmDdYyyy(eventDate),
      freqDisplay: r?.frequencyCode != null ? `${r.frequencyCode} · ${this.titleCase(r?.frequencyLabel)}` : '-',
      arAmount: this.formatCurrency(amount),
      arAmountClass: amount < -0.004 ? 'positive' : '',
      statusClass: this.statusGridClass(status),
      sourceBadgeClass: source.toLowerCase().replace(/_/g, ''),
      charges,
      payments,
      openBal: charges - payments,
      writtenOff: r?.writtenOff != null ? Number(r.writtenOff) : undefined,
      woPendingId: r?.woPendingId ?? null,
    };
  }

  private titleCase(s: string | null | undefined): string {
    const v = (s ?? '').toLowerCase().trim();
    return v ? v.charAt(0).toUpperCase() + v.slice(1) : '-';
  }

  private statusGridClass(status: LedgerStatus): string {
    const s = (status ?? '').toUpperCase();
    if (s === 'ACCEPTED' || s === 'PAID') return 'paid';
    if (s === 'REJECTED' || s === 'DENIED') return 'denied';
    if (s === 'PENDING') return 'pending';
    return 'default';
  }

  onGridAction(event: { action: string; data: LedgerRow }): void {
    if (event?.action === 'writeOff') {
      this.openWriteOff(event.data);
    }
  }

  onClear(): void {
    this.filterDosFrom = null;
    this.filterDosTo = null;
    this.filterMco = '';
    this.filterIcn = '';
    this.filterConsumer = '';
    this.searchError = '';
    this.onSearch();
  }

  // GET billingapi/v1/globalLedgerExport/{programCode} - same filters as the on-screen
  // search (DOS From/To as yyyy-MM-dd, MCO, Claim Number/PPL ICN, Consumer PPL ID), all optional.
  exportResults(): void {
    if (!this.results.length || this.exportSubmitting) {
      return;
    }

    this.exportError = '';
    this.exportSubmitting = true;
    const filters = {
      dosFrom: this.filterDosFrom ? this.toIsoDate(this.filterDosFrom) : undefined,
      dosTo: this.filterDosTo ? this.toIsoDate(this.filterDosTo) : undefined,
      mcoName: this.filterMco || undefined,
      pplIcn: this.filterIcn.trim() || undefined,
      participantPplId: this.filterConsumer.trim() || undefined,
    };

    this.billingService.exportGlobalLedger(this.programCode || 'NY', filters).subscribe({
      next: (response) => this.handleExportResponse(response),
      error: (err) => {
        this.exportSubmitting = false;
        this.logger.error('Failed to export global ledger', err);
        this.exportError = 'Could not export the ledger. Please try again.';
        this.showFeedback(this.exportError, true);
      },
    });
  }

  private handleExportResponse(response: HttpResponse<Blob>): void {
    this.exportSubmitting = false;
    const blob = response.body;
    const contentType = response.headers.get('Content-Type') ?? '';

    if (!blob || contentType.includes('application/json')) {
      this.readBlobAsJson(blob).then((errorBody) => {
        this.exportError = errorBody?.responsemsg?.[0]?.desc || errorBody?.message || 'Could not export the ledger. Please try again.';
        this.showFeedback(this.exportError, true);
      });
      return;
    }

    const filename = this.filenameFromContentDisposition(response.headers.get('Content-Disposition')) || 'global-ledger-export.xlsx';
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    this.showFeedback(`Exported ${this.results.length} rows`, false);
  }

  private readBlobAsJson(blob: Blob | null): Promise<any> {
    if (!blob) {
      return Promise.resolve(null);
    }
    return blob.text().then((text) => {
      try {
        return JSON.parse(text);
      } catch {
        return null;
      }
    });
  }

  private filenameFromContentDisposition(header: string | null): string | null {
    if (!header) {
      return null;
    }
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
    return match ? decodeURIComponent(match[1]) : null;
  }

  private toIsoDate(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  showsWriteOffButton(row: LedgerRow): boolean {
    return !row.isWrittenOff;
  }

  // ---------------------------------------------------------------------------
  // Write Off request (Ledger Entries tab)
  // ---------------------------------------------------------------------------

  openWriteOff(row: LedgerRow): void {
    const match = this.requests.find((r) => r.claimDetailId != null && r.claimDetailId === String(row.claimDetailId));
    this.woRow = match
      ? { ...row, charges: match.totalCharges, payments: match.payments, openBal: match.totalCharges - match.payments }
      : row;
    this.woReason = '';
    this.woComment = '';
    this.woReasonError = '';
    this.woSubmitError = '';
    this.showWriteOffModal = true;
  }

  closeWriteOff(): void {
    this.showWriteOffModal = false;
    this.woRow = null;
    this.woSubmitError = '';
  }

  submitWriteOff(): void {
    if (!this.woReason) {
      this.woReasonError = 'Select a reason for the write-off';
      return;
    }
    this.woReasonError = '';
    const row = this.woRow;
    if (!row || row.claimDetailId == null || this.woSubmitting) return;

    this.woSubmitting = true;
    this.woSubmitError = '';
    const payload = {
      tasBillingBatchClaimDetailId: Number(row.claimDetailId),
      reason: this.woReason,
      justification: this.woComment.trim(),
    };

    this.billingService.submitWriteOffRequest(this.programCode || 'NY', payload).subscribe({
      next: (response: any) => {
        this.woSubmitting = false;
        const result = response?.responsedata;
        if (result?.errCode != null && result.errCode !== '0') {
          this.woSubmitError = this.backendErrorMessage(response) ?? 'Failed to submit the write-off request.';
          return;
        }
        const amount = result?.writeOffAmount != null ? Number(result.writeOffAmount) : row.openBal ?? 0;
        row.woPendingId = result?.requestId != null ? String(result.requestId) : null;
        this.closeWriteOff();
        this.loadWriteOffRequests();
        this.showFeedback(`Write-off request for ${this.formatCurrency(amount)} sent for approval`, false);
      },
      error: (err) => {
        this.woSubmitting = false;
        this.logger.error('Failed to submit write-off request', err);
        this.woSubmitError = this.backendErrorMessage(err) ?? 'Failed to submit the write-off request.';
      },
    });
  }

  private backendErrorMessage(resp: any): string | null {
    return resp?.error?.responsemsg?.[0]?.desc ?? resp?.responsemsg?.[0]?.desc ?? null;
  }

  // ---------------------------------------------------------------------------
  // Write-off Approvals tab
  // ---------------------------------------------------------------------------

  loadWriteOffRequests(): void {
    const searchParam: { jsonPath: string; operator: string; value: string }[] = [];
    if (this.apStatus !== 'All') {
      searchParam.push({ jsonPath: 'status', operator: 'equal', value: this.apStatus.toUpperCase() });
    }
    const search = this.apSearch.trim();
    if (search) {
      const jsonPath = search.includes('-') ? 'participantPplId' : 'pplIcn';
      searchParam.push({ jsonPath, operator: 'equal', value: search });
    }

    const payload = {
      pageName: 'billingWriteOffRequestView',
      programCode: this.programCode || 'NY',
      recordsPerPage: 50,
      startingPageNumber: 1,
      orderByColumn: ['requestedDate'],
      orderBy: ['asc'],
      condition: 'and',
      searchParam,
    };

    this.listService.getListData(payload, 'billing').subscribe({
      next: (response: any) => {
        const rows: any[] = response?.responsedata ?? [];
        this.requests = rows.map((r) => this.mapWriteOffRequest(r));
        this.selectedIds.clear();
      },
      error: (err) => {
        this.logger.error('Failed to load write-off requests', err);
        this.requests = [];
      },
    });
  }

  private mapWriteOffRequest(r: any): WriteOffRequest {
    return {
      id: String(r?.id ?? ''),
      claimDetailId: r?.tasBillingBatchClaimDetailId != null ? String(r.tasBillingBatchClaimDetailId) : null,
      pplIcn: r?.pplIcn ?? '-',
      consumerName: '-',
      pplId: r?.participantPplId ?? '-',
      mco: r?.mcoName ?? '-',
      dos: r?.serviceDate ? new Date(r.serviceDate) : new Date(),
      totalCharges: Number(r?.totalCharges ?? 0),
      payments: Number(r?.payments ?? 0),
      arAmount: Number(r?.arAmount ?? 0),
      writeOffAmount: Number(r?.writeOffAmount ?? 0),
      reason: r?.reason ?? '-',
      requestedBy: r?.requestedByName ?? '-',
      requestedOn: r?.requestedDate ? new Date(r.requestedDate) : new Date(),
      status: this.mapWriteOffStatus(r?.status),
      decidedBy: r?.decidedByName ?? null,
      decidedOn: r?.decidedDate ? new Date(r.decidedDate) : null,
      comment: r?.decisionComment ?? r?.justification ?? '',
    };
  }

  private mapWriteOffStatus(status: any): WriteOffStatus {
    const s = (status ?? '').toString().toUpperCase();
    if (s === 'APPROVED') return 'Approved';
    if (s === 'REJECTED') return 'Rejected';
    return 'Pending';
  }

  canDecide(r: WriteOffRequest): boolean {
    return r.status === 'Pending' && r.requestedBy !== this.currentUser;
  }

  get filteredRequests(): WriteOffRequest[] {
    return this.requests
      .filter((r) => !this.apMco || r.mco === this.apMco)
      .sort((a, b) => {
        if (a.status === 'Pending' && b.status === 'Pending') return a.requestedOn.getTime() - b.requestedOn.getTime();
        const da = a.decidedOn?.getTime() ?? Infinity;
        const db = b.decidedOn?.getTime() ?? Infinity;
        return db - da;
      });
  }

  setApStatus(status: WriteOffStatus | 'All'): void {
    this.apStatus = status;
    this.selectedIds.clear();
    this.loadWriteOffRequests();
  }

  onApSearch(): void {
    this.loadWriteOffRequests();
  }

  toggleSelect(id: string, on: boolean): void {
    if (on) this.selectedIds.add(id);
    else this.selectedIds.delete(id);
  }

  isSelected(id: string): boolean {
    return this.selectedIds.has(id);
  }

  get allSelectedOnPage(): boolean {
    return this.filteredRequests.length > 0 && this.filteredRequests.every((r) => this.selectedIds.has(r.id));
  }

  selectAllOnPage(on: boolean): void {
    this.filteredRequests.forEach((r) => (on ? this.selectedIds.add(r.id) : this.selectedIds.delete(r.id)));
  }

  get selectedIdsArray(): string[] {
    return Array.from(this.selectedIds);
  }

  confirmAction: 'Approved' | 'Rejected' = 'Approved';
  confirmComment = '';
  confirmCommentError = '';
  confirmError = '';
  confirmSubmitting = false;

  openApproveConfirm(ids: string[]): void {
    this.openDecisionConfirm(ids, 'Approved');
  }

  openRejectConfirm(ids: string[]): void {
    this.openDecisionConfirm(ids, 'Rejected');
  }

  private openDecisionConfirm(ids: string[], action: 'Approved' | 'Rejected'): void {
    const decidable = ids.filter((id) => {
      const r = this.requests.find((req) => req.id === id);
      return r && this.canDecide(r);
    });
    if (!decidable.length) return;
    this.confirmAction = action;
    this.confirmIds = decidable;
    this.confirmComment = '';
    this.confirmCommentError = '';
    this.confirmError = '';
    this.showConfirmModal = true;
  }

  closeConfirm(): void {
    this.showConfirmModal = false;
    this.confirmIds = [];
    this.confirmComment = '';
    this.confirmCommentError = '';
    this.confirmError = '';
  }

  get confirmRequests(): WriteOffRequest[] {
    return this.confirmIds.map((id) => this.requests.find((r) => r.id === id)!).filter(Boolean);
  }

  get confirmTotal(): number {
    return this.confirmRequests.reduce((sum, r) => sum + r.totalCharges, 0);
  }

  get confirmMessage(): string {
    const reqs = this.confirmRequests;
    const total = this.formatCurrency(this.confirmTotal);
    const verb = this.confirmAction === 'Approved' ? 'Write off' : 'Reject the write-off for';
    return reqs.length === 1
      ? `${verb} claim ${reqs[0].pplIcn} for ${total}?`
      : `${verb} ${reqs.length} claims for ${total} in total?`;
  }

  commitDecision(): void {
    if (this.confirmAction === 'Rejected' && !this.confirmComment.trim()) {
      this.confirmCommentError = 'A comment is required to reject a write-off.';
      return;
    }
    this.confirmCommentError = '';
    if (this.confirmSubmitting) return;

    const ids = this.confirmIds.slice();
    const requestIds = ids.map((id) => Number(id)).filter((n) => !Number.isNaN(n));
    if (!requestIds.length) {
      this.closeConfirm();
      return;
    }

    const decision: 'APPROVE' | 'REJECT' = this.confirmAction === 'Approved' ? 'APPROVE' : 'REJECT';
    const comment = this.confirmComment.trim();
    const payload: { requestIds: number[]; decision: 'APPROVE' | 'REJECT'; comment?: string } = {
      requestIds,
      decision,
    };
    if (comment) {
      payload.comment = comment;
    }

    this.confirmSubmitting = true;
    this.confirmError = '';
    this.billingService.submitWriteOffDecision(this.programCode || 'NY', payload).subscribe({
      next: (response: any) => {
        this.confirmSubmitting = false;
        const result = response?.responsedata;
        if (result?.errCode != null && result.errCode !== '0') {
          this.confirmError = this.backendErrorMessage(response) ?? 'Failed to process the decision.';
          return;
        }
        const processed = result?.processedRows ?? ids.length;
        const amount = result?.writtenOffAmount != null ? Number(result.writtenOffAmount) : 0;
        this.closeConfirm();
        ids.forEach((id) => this.selectedIds.delete(id));
        this.loadWriteOffRequests();
        if (decision === 'APPROVE') {
          if (this.searched) this.onSearch();
          this.showFeedback(
            `✔ ${processed} write-off${processed > 1 ? 's' : ''} approved · ${this.formatCurrency(amount)} posted to Ledger Entries`,
            false
          );
        } else {
          this.showFeedback(`${processed} write-off request${processed > 1 ? 's' : ''} rejected`, false);
        }
      },
      error: (err) => {
        this.confirmSubmitting = false;
        this.logger.error('Failed to submit write-off decision', err);
        this.confirmError = this.backendErrorMessage(err) ?? 'Failed to process the decision.';
      },
    });
  }

  formatCurrency(n: number): string {
    const sign = n < -0.004 ? '−' : '';
    return `${sign}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  private showFeedback(message: string, isError: boolean): void {
    this.feedback = message;
    this.feedbackIsError = isError;
  }
}
